import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, signOut, User, Auth, createUserWithEmailAndPassword, EmailAuthProvider, reauthenticateWithCredential, updatePassword, browserLocalPersistence, setPersistence } from 'firebase/auth';
import { getFirestore, collection, getDocs, query, where, setDoc, doc, updateDoc } from 'firebase/firestore';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private app: FirebaseApp | null = null;
  private auth: Auth | null = null;
  private db: any = null;
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  user$ = this.currentUserSubject.asObservable();
  private roleSubject = new BehaviorSubject<'admin' | 'staff' | null>(null);
  role$ = this.roleSubject.asObservable();

  constructor() {
    this.initializeFirebase();
  }

  private initializeFirebase(): void {
    if (typeof window === 'undefined') {
      return;
    }
    this.app = getApps().length ? getApps()[0] : initializeApp(environment.firebaseConfig);
    this.auth = getAuth(this.app);
    this.db = getFirestore(this.app);

    setPersistence(this.auth, browserLocalPersistence).catch(() => undefined);

    onAuthStateChanged(this.auth, (user) => {
      this.currentUserSubject.next(user);
      if (user) {
        this.resolveRole(user.email || '').subscribe();
      } else {
        this.roleSubject.next(null);
      }
    });
  }

  private ensureAuth(): Auth {
    if (!this.auth) {
      this.initializeFirebase();
    }
    return this.auth as Auth;
  }

  login(email: string, password: string): Promise<void> {
    return signInWithEmailAndPassword(this.ensureAuth(), email, password).then(() => undefined);
  }

  logout(): Promise<void> {
    return signOut(this.ensureAuth()).then(() => undefined);
  }

  isAuthenticated(): boolean {
    return !!this.currentUserSubject.getValue();
  }

  isAdminEmail(email: string): boolean {
    return email.toLowerCase() === environment.adminEmail.toLowerCase();
  } 

  resolveRole(email: string): Observable<'admin' | 'staff' | null> {
    return new Observable((observer) => {
      if (this.isAdminEmail(email)) {
        this.roleSubject.next('admin');
        observer.next('admin');
        observer.complete();
        return;
      }
      const q = query(collection(this.db, 'authorized_users'), where('email', '==', email));
      getDocs(q).then((snapshot) => {
        const found = snapshot.docs[0]?.data();
        const role = found?.['role'] as 'staff' | 'admin' | undefined;
        this.roleSubject.next(role || null);
        observer.next(role || null);
        observer.complete();
      }).catch((err) => {
        console.warn('Authorized users lookup failed', err);
        this.roleSubject.next(null);
        observer.next(null);
        observer.complete();
      });
    });
  }

  async registerStaff(email: string, role: 'admin' | 'staff', password: string, fullName?: string): Promise<void> {
    const auth = this.ensureAuth();
    if (!this.isAdminEmail(auth.currentUser?.email || '')) {
      throw new Error('Only the administrator can register staff members.');
    }
    const created = await createUserWithEmailAndPassword(auth, email, password);
    const uid = created.user.uid;
    await setDoc(doc(this.db, 'users', uid), { uid, email, role, fullName: fullName || email.split('@')[0], isActive: 1, createdAt: new Date().toISOString() });
  }

  async updateStaff(uid: string, data: { email: string; role: 'admin' | 'staff'; fullName?: string }): Promise<void> {
    if (!this.auth?.currentUser || !this.isAdminEmail(this.auth.currentUser.email || '')) {
      throw new Error('Only the administrator can update staff members.');
    }
    await updateDoc(doc(this.db, 'users', uid), { ...data, updatedAt: new Date().toISOString() });
  }

  async deleteStaff(uid: string): Promise<void> {
    if (!this.auth?.currentUser || !this.isAdminEmail(this.auth.currentUser.email || '')) {
      throw new Error('Only the administrator can delete staff members.');
    }
    await updateDoc(doc(this.db, 'users', uid), { isActive: 0, updatedAt: new Date().toISOString() });
  }

  async getStaffUsers(): Promise<Array<{ uid: string; email: string; role: string; fullName: string }>> {
    const q = query(collection(this.db, 'users'));
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((docSnap) => ({ uid: docSnap.id, ...(docSnap.data() as { uid?: string; email?: string; role?: string; fullName?: string; isActive?: number }) }))
      .filter((item): item is { uid: string; email: string; role: string; fullName?: string; isActive?: number } => Boolean(item.email))
      .filter((item) => item.isActive !== 0)
      .map((item) => ({ uid: item.uid || '', email: item.email || '', role: item.role || 'staff', fullName: item.fullName || item.email.split('@')[0] }));
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const auth = this.ensureAuth();
    const user = auth.currentUser;
    if (!user?.email) {
      throw new Error('No active account found.');
    }

    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  }
}
